import React, { useCallback, useEffect, useRef, useState } from 'react';

import { responderPesquisaTotem, verificarPesquisaTotem } from '../../api/formsApi';
import { PesquisaPublicaView } from '../formularios/interfaces/forms';
import CampoDaPesquisa from '../pesquisa_publica/CampoDaPesquisa';
import { formatPhoneBr } from '../../utils/phoneMask';
import logoBackground from '../auth/pages/css/img/Atual Clube - Marca_Laranja 1.svg';
import logo from '../auth/pages/css/img/logo.svg';
// Casco visual das telas do associado (cp-*) e estilos dos campos (pp-*):
// importados, nunca recopiados — link, totem e checklist são a mesma identidade.
import '../checklist_publico/ChecklistPublicoPage.css';
import '../pesquisa_publica/PesquisaPublicaPage.css';
import './PesquisaTotemPage.css';

type Respostas = Record<string, string | string[]>;

/** Depois do agradecimento, volta sozinho para a tela inicial (próximo da fila). */
const RESET_POS_ENVIO_MS = 8_000;
/** Abandono no meio do preenchimento: limpa tudo — a tela é compartilhada. */
const RESET_POR_INATIVIDADE_MS = 3 * 60_000;

const mensagemDoErro = (erro: any, padrao: string): string => {
  const doBackend = erro?.response?.data?.message;
  if (typeof doBackend === 'string' && doBackend.trim()) return doBackend;
  return padrao;
};

/**
 * Totem da oficina (`/pesquisa-totem`, ADR 0032): o associado responde a
 * pesquisa num dispositivo fixo, identificando-se por PLACA + TELEFONE — não há
 * token nem login. Quem lê é o cliente, de pé: hints inline (`cp-hint`), botões
 * grandes e reset automático entre um associado e outro.
 */
const PesquisaTotemPage: React.FC = () => {
  const [plate, setPlate] = useState('');
  const [phone, setPhone] = useState('');
  const [verificando, setVerificando] = useState(false);
  const [erroDaVerificacao, setErroDaVerificacao] = useState('');
  const [view, setView] = useState<PesquisaPublicaView | null>(null);
  const [respostas, setRespostas] = useState<Respostas>({});
  const [enviando, setEnviando] = useState(false);
  const [erroDoEnvio, setErroDoEnvio] = useState('');
  const [concluida, setConcluida] = useState(false);
  const timerInatividade = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Volta ao estado inicial — o totem é de todo mundo, nada pode sobrar na tela. */
  const reiniciar = useCallback(() => {
    setPlate('');
    setPhone('');
    setErroDaVerificacao('');
    setView(null);
    setRespostas({});
    setErroDoEnvio('');
    setConcluida(false);
  }, []);

  // Agradecimento fica RESET_POS_ENVIO_MS na tela e o totem volta para o próximo.
  useEffect(() => {
    if (!concluida) return;
    const timer = setTimeout(reiniciar, RESET_POS_ENVIO_MS);
    return () => clearTimeout(timer);
  }, [concluida, reiniciar]);

  // Abandono no meio (a pessoa foi embora): qualquer interação re-arma o timer;
  // o estouro limpa dados digitados e respostas — o próximo não pode vê-los.
  useEffect(() => {
    const rearmar = () => {
      if (timerInatividade.current) clearTimeout(timerInatividade.current);
      timerInatividade.current = setTimeout(reiniciar, RESET_POR_INATIVIDADE_MS);
    };
    rearmar();
    window.addEventListener('pointerdown', rearmar);
    window.addEventListener('keydown', rearmar);
    return () => {
      if (timerInatividade.current) clearTimeout(timerInatividade.current);
      window.removeEventListener('pointerdown', rearmar);
      window.removeEventListener('keydown', rearmar);
    };
  }, [reiniciar]);

  const handleVerificar = async (e: React.FormEvent) => {
    e.preventDefault();

    const digitosDoTelefone = phone.replace(/\D/g, '');
    if (plate.trim().length < 7) {
      setErroDaVerificacao('Informe a placa completa do veículo.');
      return;
    }
    if (digitosDoTelefone.length < 10) {
      setErroDaVerificacao('Informe o celular completo, com DDD.');
      return;
    }

    setVerificando(true);
    setErroDaVerificacao('');
    try {
      const dados = await verificarPesquisaTotem(plate.trim(), digitosDoTelefone);
      setView(dados);
    } catch (erro: any) {
      setErroDaVerificacao(
        mensagemDoErro(erro, 'Não foi possível verificar os dados informados. Tente novamente.'),
      );
    } finally {
      setVerificando(false);
    }
  };

  const handleEnviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!view) return;

    const faltando = view.template.fields.find((campo) => {
      if (!campo.required) return false;
      const valor = respostas[campo.id];
      return valor === undefined || valor === '' || (Array.isArray(valor) && valor.length === 0);
    });
    if (faltando) {
      setErroDoEnvio(`Responda o campo "${faltando.label}" para enviar.`);
      return;
    }

    setEnviando(true);
    setErroDoEnvio('');
    try {
      const answers = view.template.fields
        .filter((campo) => {
          const valor = respostas[campo.id];
          return valor !== undefined && valor !== '' && !(Array.isArray(valor) && valor.length === 0);
        })
        .map((campo) => ({ fieldId: campo.id, value: respostas[campo.id] }));

      await responderPesquisaTotem(plate.trim(), phone.replace(/\D/g, ''), answers);
      setConcluida(true);
    } catch (erro: any) {
      setErroDoEnvio(mensagemDoErro(erro, 'Não foi possível enviar suas respostas. Tente novamente.'));
    } finally {
      setEnviando(false);
    }
  };

  const mostrandoFormulario = Boolean(view) && !concluida;

  return (
    <div className="cp-page pt-page">
      <div className="logo-background">
        <img className="logo-background-atual" src={logoBackground} alt="logo de fundo" />
      </div>

      <div className="cp-container-wrapper">
        <img className="cp-logo" src={logo} alt="Logo Atual" />

        <div className={`cp-container ${!mostrandoFormulario ? 'cp-container--auth' : 'cp-container--full'}`}>
          <div className="cp-header">
            <h1 className="cp-title">Pesquisa de Satisfação</h1>

            {mostrandoFormulario && view && (
              <div className="cp-vehicle-card">
                <div className="cp-vehicle-details">
                  <span className="cp-vehicle-label">Veículo</span>
                  <span className="cp-vehicle-name">{view.car.model || 'Veículo'}</span>
                </div>
                <div className="cp-plate-badge">
                  <div className="cp-plate-header">
                    <span>BRASIL</span>
                  </div>
                  <div className="cp-plate-number">{view.car.plate}</div>
                </div>
              </div>
            )}
          </div>

          {concluida && (
            <div className="pp-final">
              <h2 className="pp-final-titulo">Respostas enviadas</h2>
              <p className="pp-final-texto">
                Obrigado por avaliar o atendimento. Sua opinião ajuda a oficina a melhorar.
              </p>
              <p className="cp-hint">Esta tela volta ao início em alguns segundos.</p>
            </div>
          )}

          {!view && !concluida && (
            <form className="cp-form-auth" onSubmit={handleVerificar}>
              <div className="cp-auth-div">
                <div className="cp-auth-title">
                  <h1 className="cp-h1-auth">Avalie seu atendimento</h1>
                  <p className="cp-p-auth">
                    Informe a placa do veículo e o celular cadastrado na oficina para começar.
                  </p>
                  <p className="cp-hint">
                    A pesquisa fica disponível por 30 dias após a entrega do veículo, e pode ser
                    respondida uma única vez. Se os dados não forem aceitos, procure a recepção para
                    conferir o cadastro.
                  </p>
                </div>

                <div className="cp-auth-fields">
                  <p className="cp-field-label">Placa do veículo</p>
                  <div className="nice-form-group auth">
                    <input
                      type="text"
                      placeholder="ABC1D23"
                      value={plate}
                      onChange={(e) =>
                        setPlate(e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 7))
                      }
                      autoFocus
                      required
                      className="nice-input pt-input-placa"
                      autoComplete="off"
                    />
                  </div>

                  <p className="cp-field-label">Celular</p>
                  <div className="nice-form-group auth">
                    <input
                      type="tel"
                      placeholder="(21) 99999-8888"
                      value={phone}
                      onChange={(e) => setPhone(formatPhoneBr(e.target.value))}
                      required
                      className="nice-input"
                      autoComplete="off"
                    />
                  </div>
                </div>

                {erroDaVerificacao && <div className="cp-error-box">{erroDaVerificacao}</div>}

                <div className="cp-button-div auth">
                  <button type="submit" disabled={verificando} className="cp-btn-primary">
                    {verificando ? 'Verificando...' : 'Começar'}
                  </button>
                </div>
              </div>
            </form>
          )}

          {mostrandoFormulario && view && (
            <form className="pp-formulario" onSubmit={handleEnviar}>
              {view.template.description && <p className="pp-descricao">{view.template.description}</p>}

              {view.template.fields.map((campo) => (
                <CampoDaPesquisa
                  key={campo.id}
                  campo={campo}
                  valor={respostas[campo.id]}
                  onChange={(valor) => setRespostas((atual) => ({ ...atual, [campo.id]: valor }))}
                />
              ))}

              {erroDoEnvio && <div className="cp-error-box">{erroDoEnvio}</div>}

              <div className="cp-button-div pt-acoes">
                <button type="button" className="pt-btn-recomecar" onClick={reiniciar} disabled={enviando}>
                  Recomeçar
                </button>
                <button type="submit" disabled={enviando} className="cp-btn-primary">
                  {enviando ? 'Enviando...' : 'Enviar respostas'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default PesquisaTotemPage;
